/*
 * Fig & Bloom - reminder email checkout attribution (Option C, theme #590).
 *
 * A Birthday Club reminder link lands with ?em=em_<32 lowercase hex> (the
 * EmailMessage measurement id, src/lib/email-library/measurement.ts; the same
 * /^em_[a-f0-9]{32}$/ assets/fb-reminders.js and funnel-sync.ts accept). This
 * file carries that id to Shopify so Birthday Club can count checkouts and
 * orders per email from Shopify's own data, with no checkout pixel change:
 *
 *   landing      any page whose URL carries a valid em stores
 *                localStorage fb_bc_em = { em, at } (at = landing time, ms).
 *                The newest landing wins. Format check only: the id is opaque
 *                and Birthday Club ignores one it did not send. (Waiting for
 *                the Reminders token to resolve would lose it on any page
 *                without the Reminders bundle, and on preview hosts, which the
 *                API's CORS blocks.)
 *
 *   add to cart  after any successful /cart/add|change|update|clear request
 *                (fetch or XHR, whoever issued it), and once on page load, a
 *                cart with items gets the private cart attributes
 *                  _bc_em     the stored em
 *                  _bc_em_at  the landing time, ISO 8601 UTC
 *                through POST /cart/update.js, which merges attributes: keys
 *                not named are left alone, so _ph_session_id, _ph_distinct_id,
 *                _fb_reminder_ctx, _fb_person_notes, the pickup attributes and
 *                the cart note are untouched. Written only when the cart has
 *                none, or an older one (_bc_em_at before the stored landing).
 *                Shopify copies cart attributes to the checkout (abandoned
 *                checkouts' customAttributes) and the order (note_attributes).
 *                The leading underscore hides them at checkout; they show in
 *                the admin order page only.
 *
 * Nothing is written to an empty cart, and nothing at all for visitors who
 * never followed a reminder link (no stored em means no /cart.js read).
 * A landing older than ATTRIBUTION_DAYS is dropped, matching Birthday Club's
 * 7-day click window. Our own writes use the fetch captured before this file
 * wraps it, so they never trigger another sync; other scripts' attribute
 * writes do trigger one, which then finds nothing to change.
 *
 * Known gaps: Buy it now / accelerated checkout buttons skip the cart, so
 * those checkouts carry no cart attributes; a flow that adds and navigates to
 * /checkout in the same tick (direct checkout) can leave before the write.
 * Both apply equally to the existing _ph_* attributes.
 *
 * Tests: tests/fb-bc-em.test.js
 */
(function (root) {
  'use strict';
  if (!root || root.FBBcEm) return;

  var KEY = 'fb_bc_em';
  var ATTR = '_bc_em';
  var ATTR_AT = '_bc_em_at';
  var EM = /^em_[a-f0-9]{32}$/;
  var ATTRIBUTION_DAYS = 7;
  var DAY_MS = 86400000;
  var CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?$/;

  var nativeFetch = typeof root.fetch === 'function' ? root.fetch : null;

  function storage() {
    try { return root.localStorage || null; } catch (_) { return null; }
  }

  function now() { return Date.now(); }

  /* First em value in a query string, or null. Never throws. */
  function emFromSearch(search) {
    var raw = String(search || '');
    if (raw.charAt(0) === '?') raw = raw.slice(1);
    if (!raw || raw.length > 4096) return null;
    var pairs = raw.split('&');
    for (var i = 0; i < pairs.length; i++) {
      var pair = pairs[i];
      var idx = pair.indexOf('=');
      if (idx === -1) continue;
      var key;
      var value;
      try {
        key = decodeURIComponent(pair.slice(0, idx));
        value = decodeURIComponent(pair.slice(idx + 1));
      } catch (_) { continue; }
      if (key !== 'em') continue;
      return EM.test(value) ? value : null; // first em only, like fb-reminders.js
    }
    return null;
  }

  /* The stored landing, or null when missing, malformed or too old. */
  function readLanding(at) {
    var store = storage();
    if (!store) return null;
    var instant = typeof at === 'number' ? at : now();
    try {
      var stored = JSON.parse(store.getItem(KEY) || 'null');
      if (!stored || typeof stored.em !== 'string' || !EM.test(stored.em) || typeof stored.at !== 'number' || !isFinite(stored.at)) {
        if (stored !== null) store.removeItem(KEY);
        return null;
      }
      if (stored.at > instant + 300000 || instant - stored.at > ATTRIBUTION_DAYS * DAY_MS) {
        store.removeItem(KEY);
        return null;
      }
      return stored;
    } catch (_) {
      try { store.removeItem(KEY); } catch (__) {}
      return null;
    }
  }

  /* Stores the URL's em as the newest landing. Returns the landing or null. */
  function captureLanding(search, at) {
    var em = emFromSearch(search);
    if (!em) return null;
    var store = storage();
    if (!store) return null;
    var record = { em: em, at: typeof at === 'number' ? at : now() };
    try { store.setItem(KEY, JSON.stringify(record)); } catch (_) { return null; }
    return record;
  }

  function cartAttributeTime(value) {
    if (typeof value !== 'string' || !value) return NaN;
    return Date.parse(value);
  }

  /* The attributes to write, or null when the cart already has this or a newer landing. */
  function desiredAttributes(cart, landing) {
    if (!landing || !cart || !(cart.item_count > 0)) return null;
    var attrs = cart.attributes || {};
    var currentAt = cartAttributeTime(attrs[ATTR_AT]);
    var hasCurrent = typeof attrs[ATTR] === 'string' && EM.test(attrs[ATTR]) && isFinite(currentAt);
    if (hasCurrent && currentAt >= landing.at) return null; // keep the more recent one
    var out = {};
    out[ATTR] = landing.em;
    out[ATTR_AT] = new Date(landing.at).toISOString();
    return out;
  }

  function cartRoot() {
    var r = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  var inFlight = null;
  var runAgain = false;

  /* Reads the cart and writes the attributes if needed. Resolves true if it
     wrote. Never rejects. Overlapping calls coalesce into one re-read. */
  function sync() {
    if (!nativeFetch) return Promise.resolve(false);
    if (!readLanding()) return Promise.resolve(false);
    if (inFlight) { runAgain = true; return inFlight; }
    inFlight = nativeFetch.call(root, cartRoot() + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (res) { return res && res.ok ? res.json() : null; })
      .then(function (cart) {
        var landing = readLanding();
        var attrs = desiredAttributes(cart, landing);
        if (!attrs) return false;
        return nativeFetch.call(root, cartRoot() + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: attrs })
        }).then(function (res) { return !!(res && res.ok); });
      })
      .catch(function () { return false; })
      .then(function (wrote) {
        inFlight = null;
        if (runAgain) {
          runAgain = false;
          return sync().then(function (again) { return wrote || again; });
        }
        return wrote;
      });
    return inFlight;
  }

  function isCartMutationUrl(url) {
    if (!url) return false;
    var path;
    try { path = new root.URL(String(url), root.location && root.location.origin).pathname; } catch (_) { path = String(url).split('?')[0]; }
    return CART_MUTATION.test(path);
  }

  function watchCartMutations() {
    if (nativeFetch) {
      root.fetch = function (input) {
        var url = typeof input === 'string' ? input : input && input.url;
        var result = nativeFetch.apply(this, arguments);
        if (isCartMutationUrl(url) && result && typeof result.then === 'function') {
          result.then(function (res) { if (res && res.ok) sync(); }, function () {});
        }
        return result;
      };
    }
    var XHR = root.XMLHttpRequest;
    if (typeof XHR === 'function' && XHR.prototype) {
      var open = XHR.prototype.open;
      var send = XHR.prototype.send;
      XHR.prototype.open = function (method, url) {
        this.__fbBcEmCart = isCartMutationUrl(url);
        return open.apply(this, arguments);
      };
      XHR.prototype.send = function () {
        var xhr = this;
        if (xhr.__fbBcEmCart && typeof xhr.addEventListener === 'function') {
          xhr.addEventListener('load', function () { if (xhr.status >= 200 && xhr.status < 300) sync(); });
        }
        return send.apply(this, arguments);
      };
    }
  }

  try { captureLanding(root.location && root.location.search); } catch (_) {}
  watchCartMutations();
  sync();

  root.FBBcEm = {
    _test: {
      KEY: KEY, ATTR: ATTR, ATTR_AT: ATTR_AT, ATTRIBUTION_DAYS: ATTRIBUTION_DAYS,
      emFromSearch: emFromSearch, readLanding: readLanding, captureLanding: captureLanding,
      desiredAttributes: desiredAttributes, isCartMutationUrl: isCartMutationUrl, sync: sync
    }
  };
})(typeof window !== 'undefined' ? window : this);
